import { EarlyAccessDialog } from "@/components/EarlyAccessDialog";
import { Layout } from "@/components/Layout";
import { TradeConfidence } from "@/components/TradeConfidence";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle, Users, Package, Recycle, ArrowRight, Search, FileText, BadgeCheck, EyeOff, Eye, Route, Lock, Scale, Handshake } from "lucide-react";
import platformRfps from "@/assets/platform-rfps.jpg";
import platformRfpDetail from "@/assets/platform-rfp-detail.jpg";
import platformCatalog from "@/assets/platform-catalog.jpg";
import platformListing from "@/assets/platform-listing.jpg";
import { useCallback, useRef, useState, type RefObject } from "react";
import { Helmet } from "react-helmet-async";
import { useLanguage } from "@/contexts/LanguageContext";

export default function HowItWorks() {
  const { t } = useLanguage();
  const browseContentRef = useRef<HTMLDivElement>(null);
  const remakeContentRef = useRef<HTMLDivElement>(null);
  const [serviceTab, setServiceTab] = useState<"browse" | "remake">("browse");
  const [browseSubTab, setBrowseSubTab] = useState<"sell" | "buy" | null>(null);
  const [remakeSubTab, setRemakeSubTab] = useState<"post" | "bid" | null>(null);

  const scrollToRef = useCallback((ref: RefObject<HTMLElement>) => {
    const getOffset = () => {
      const nav = document.querySelector("nav");
      return (nav?.getBoundingClientRect().height ?? 0) + 24;
    };
    const attempt = (triesLeft: number) => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.height === 0 && triesLeft > 0) { requestAnimationFrame(() => attempt(triesLeft - 1)); return; }
      window.scrollTo({ top: Math.max(0, window.scrollY + rect.top - getOffset()), behavior: "smooth" });
    };
    requestAnimationFrame(() => attempt(12));
  }, []);

  const handleTabChange = (value: "browse" | "remake") => {
    setServiceTab(value);
    if (value === "browse") setBrowseSubTab(null);
    if (value === "remake") setRemakeSubTab(null);
    setTimeout(() => scrollToRef(value === "browse" ? browseContentRef : remakeContentRef), 50);
  };

  const browseDeadstock = {
    sellers: {
      title: t("howItWorks.sellersTitle"),
      description: t("howItWorks.sellersDesc"),
      icon: <Package className="h-6 w-6" />,
      steps: [
        { step: "1", title: t("howItWorks.sellStep1Title"), description: t("howItWorks.sellStep1Desc") },
        { step: "2", title: t("howItWorks.sellStep2Title"), description: t("howItWorks.sellStep2Desc") },
        { step: "3", title: t("howItWorks.sellStep3Title"), description: t("howItWorks.sellStep3Desc") },
      ],
      benefits: [t("howItWorks.sellBenefit1"), t("howItWorks.sellBenefit2"), t("howItWorks.sellBenefit3"), t("howItWorks.sellBenefit4")]
    },
    buyers: {
      title: t("howItWorks.buyersTitle"),
      description: t("howItWorks.buyersDesc"),
      icon: <Users className="h-6 w-6" />,
      steps: [
        { step: "1", title: t("howItWorks.buyStep1Title"), description: t("howItWorks.buyStep1Desc") },
        { step: "2", title: t("howItWorks.buyStep2Title"), description: t("howItWorks.buyStep2Desc") },
        { step: "3", title: t("howItWorks.buyStep3Title"), description: t("howItWorks.buyStep3Desc") },
      ],
      benefits: [t("howItWorks.buyBenefit1"), t("howItWorks.buyBenefit2"), t("howItWorks.buyBenefit3"), t("howItWorks.buyBenefit4")]
    }
  };

  const remakeProjects = {
    brands: {
      title: t("howItWorks.brandsTitle"),
      description: t("howItWorks.brandsDesc"),
      icon: <FileText className="h-6 w-6" />,
      steps: [
        { step: "1", title: t("howItWorks.postStep1Title"), description: t("howItWorks.postStep1Desc") },
        { step: "2", title: t("howItWorks.postStep2Title"), description: t("howItWorks.postStep2Desc") },
        { step: "3", title: t("howItWorks.postStep3Title"), description: t("howItWorks.postStep3Desc") },
      ],
      benefits: [t("howItWorks.postBenefit1"), t("howItWorks.postBenefit2"), t("howItWorks.postBenefit3"), t("howItWorks.postBenefit4")]
    },
    remakers: {
      title: t("howItWorks.remakersTitle"),
      description: t("howItWorks.remakersDesc"),
      icon: <Recycle className="h-6 w-6" />,
      steps: [
        { step: "1", title: t("howItWorks.bidStep1Title"), description: t("howItWorks.bidStep1Desc") },
        { step: "2", title: t("howItWorks.bidStep2Title"), description: t("howItWorks.bidStep2Desc") },
        { step: "3", title: t("howItWorks.bidStep3Title"), description: t("howItWorks.bidStep3Desc") },
      ],
      benefits: [t("howItWorks.bidBenefit1"), t("howItWorks.bidBenefit2"), t("howItWorks.bidBenefit3"), t("howItWorks.bidBenefit4")]
    }
  };

  const renderFlowSection = (
    data: { title: string; description: string; icon: React.ReactNode; steps: { step: string; title: string; description: string }[]; benefits: string[] },
    colorClass: string, bgColorClass: string
  ) => (
    <div className="mb-12 last:mb-0">
      <div className="flex items-center gap-4 mb-6">
        <div className={`p-3 rounded-full ${bgColorClass}`}>{data.icon}</div>
        <div>
          <h3 className="text-2xl font-bold">{data.title}</h3>
          <p className="text-muted-foreground">{data.description}</p>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        {data.steps.map((step, index) => (
          <Card key={index} className="border-2">
            <CardContent className="p-5">
              <div className={`w-8 h-8 ${bgColorClass} rounded-full flex items-center justify-center font-bold mb-3 text-sm`}>{step.step}</div>
              <h4 className="text-lg font-semibold mb-2">{step.title}</h4>
              <p className="text-muted-foreground text-sm">{step.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="bg-muted/50 rounded-xl p-6">
        <h4 className="font-semibold mb-3 flex items-center gap-2">
          <CheckCircle className={`h-5 w-5 ${colorClass}`} />
          {t("howItWorks.benefits")}
        </h4>
        <div className="grid sm:grid-cols-2 gap-2">
          {data.benefits.map((benefit, index) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span>{benefit}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      <Helmet>
        <title>How HIDNstock Works — Buy, Sell & Remake Textiles</title>
        <meta name="description" content="Two services on one platform: buy and sell sorted secondhand and deadstock textiles, or post and bid on remake projects. Here's how it works." />
        <link rel="canonical" href="https://hidnstock.com/how-it-works" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://hidnstock.com/how-it-works" />
        <meta property="og:title" content="How HIDNstock Works — Buy, Sell & Remake Textiles" />
        <meta property="og:description" content="Two services on one platform: buy and sell sorted secondhand and deadstock textiles, or post and bid on remake projects." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Tabs value={serviceTab} onValueChange={(v) => handleTabChange(v as "browse" | "remake")}>
          <section className="relative py-16 lg:py-24 overflow-hidden bg-gradient-sand-deep bg-grain text-foreground border-b-4 border-foreground">
            <div className="container mx-auto px-6 relative">
              <div className="text-center max-w-4xl mx-auto">
                <Badge variant="outline" className="mb-6 text-sm bg-transparent border-foreground/30 text-foreground"><span className="block text-center">{t("howItWorks.badgeLine1")}<br />{t("howItWorks.badgeLine2")}</span></Badge>
                <h1 className="font-black uppercase tracking-tight leading-[0.85] mb-6 text-6xl sm:text-7xl lg:text-8xl xl:text-9xl">{t("howItWorks.title")}</h1>
                <p className="text-xl text-foreground/70 leading-relaxed mb-8">{t("howItWorks.subtitle")}</p>
                <div className="max-w-2xl mx-auto">
                  <TabsList className="grid w-full grid-cols-2 h-auto p-1.5">
                    <TabsTrigger value="browse"
                      onClick={() => { if (serviceTab === "browse") scrollToRef(browseContentRef); }}
                      className="py-4 px-3 sm:py-4 sm:px-6 text-sm sm:text-base data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:scale-[1.02] data-[state=inactive]:scale-100 data-[state=inactive]:opacity-90 data-[state=inactive]:hover:bg-primary data-[state=inactive]:hover:text-primary-foreground data-[state=inactive]:hover:opacity-100 data-[state=active]:hover:bg-primary transition-all duration-200 flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-2 min-h-[4.5rem] sm:min-h-0 cursor-pointer">
                      <Search className="h-5 w-5 flex-shrink-0" />
                      <span className="text-center leading-snug">
                        <span className="block">{t("howItWorks.tabBrowseTitle")}</span>
                        <span className="block text-xs sm:text-sm opacity-80">{t("howItWorks.tabBrowseSub")}</span>
                      </span>
                    </TabsTrigger>
                    <TabsTrigger value="remake"
                      onClick={() => { if (serviceTab === "remake") scrollToRef(remakeContentRef); }}
                      className="py-4 px-3 sm:py-4 sm:px-6 text-sm sm:text-base data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground data-[state=active]:scale-[1.02] data-[state=inactive]:scale-100 data-[state=inactive]:opacity-90 data-[state=inactive]:hover:bg-secondary data-[state=inactive]:hover:text-secondary-foreground data-[state=inactive]:hover:opacity-100 data-[state=active]:hover:bg-secondary transition-all duration-200 flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-2 min-h-[4.5rem] sm:min-h-0 cursor-pointer">
                      <FileText className="h-5 w-5 flex-shrink-0" />
                      <span className="text-center leading-snug">
                        <span className="block">{t("howItWorks.tabRemakeTitle")}</span>
                        <span className="block text-xs sm:text-sm opacity-80">{t("howItWorks.tabRemakeSub")}</span>
                      </span>
                    </TabsTrigger>
                  </TabsList>
                </div>
              </div>
            </div>
          </section>

          <section className="relative pt-16 pb-16 bg-gradient-to-b from-background to-secondary/40 overflow-hidden">
            <div className="container mx-auto px-6 relative max-w-5xl">
              <TabsContent value="browse" className="mt-0">
                <div ref={browseContentRef} className="text-center mb-10 scroll-mt-24">
                  <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">{t("howItWorks.marketplaceBadge")}</Badge>
                  <h2 className="text-3xl font-bold mb-3">{t("howItWorks.marketplaceTitle")}</h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto mb-8">{t("howItWorks.marketplaceDesc")}</p>
                  <div className="flex justify-center gap-4 mb-10">
                    <Button variant={browseSubTab === "sell" ? "default" : "outline"} size="lg" onClick={() => setBrowseSubTab("sell")} className="min-w-[140px]">
                      <Package className="h-4 w-4 mr-2" />{t("howItWorks.iWantToSell")}
                    </Button>
                    <Button variant={browseSubTab === "buy" ? "default" : "outline"} size="lg" onClick={() => setBrowseSubTab("buy")} className="min-w-[140px]">
                      <Users className="h-4 w-4 mr-2" />{t("howItWorks.iWantToBuy")}
                    </Button>
                  </div>
                </div>
                {browseSubTab === "sell" && renderFlowSection(browseDeadstock.sellers, "text-primary", "bg-primary text-primary-foreground")}
                {browseSubTab === "buy" && renderFlowSection(browseDeadstock.buyers, "text-primary", "bg-primary/20 text-primary")}
              </TabsContent>

              <TabsContent value="remake" className="mt-0">
                <div ref={remakeContentRef} className="text-center mb-10 scroll-mt-24">
                  <Badge className="mb-4 bg-secondary text-[hsl(36,25%,35%)] border-[hsl(36,25%,55%)]">{t("howItWorks.projectBadge")}</Badge>
                  <h2 className="text-3xl font-bold mb-3">{t("howItWorks.projectTitle")}</h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto mb-8">{t("howItWorks.projectDesc")}</p>
                  <div className="flex justify-center gap-4 mb-10">
                    <Button variant={remakeSubTab === "post" ? "secondary" : "outline"} size="lg" onClick={() => setRemakeSubTab("post")} className="min-w-[140px]">
                      <FileText className="h-4 w-4 mr-2" />{t("howItWorks.iWantToPost")}
                    </Button>
                    <Button variant={remakeSubTab === "bid" ? "secondary" : "outline"} size="lg" onClick={() => setRemakeSubTab("bid")} className="min-w-[140px]">
                      <Recycle className="h-4 w-4 mr-2" />{t("howItWorks.iWantToBid")}
                    </Button>
                  </div>
                </div>
                {remakeSubTab === "post" && renderFlowSection(remakeProjects.brands, "text-secondary", "bg-secondary text-secondary-foreground")}
                {remakeSubTab === "bid" && renderFlowSection(remakeProjects.remakers, "text-secondary", "bg-secondary/20 text-secondary")}
              </TabsContent>
            </div>
          </section>
        </Tabs>

        {/* Inside the platform */}
        <section className="py-20 border-t-4 border-foreground bg-background">
          <div className="container mx-auto px-6 max-w-6xl">
            <p className="text-sm font-bold uppercase mb-3">02 / Inside the platform{serviceTab === "browse" ? " / Buy & Sell" : " / Post & Bid"}</p>
            <h2 className="text-4xl md:text-6xl font-black uppercase leading-[0.9] mb-12">See it in action</h2>
            <div className="grid md:grid-cols-2 gap-8">
                {(serviceTab === "remake" ? [
                  { img: platformRfps, title: "Remake projects", text: "Brands post remake briefs with budget, timeline and location. Verified remakers browse, filter and bid." },
                  { img: platformRfpDetail, title: "Project detail", text: "Every brief shows who posted it, the deadline and a verified badge. Message the brand and send your bid in one place." },
                ] : [
                  { img: platformCatalog, title: "Browse excess", text: "Curated drops and every verified lot in one place. Search and filter by material, category, condition and location." },
                  { img: platformListing, title: "Lot detail", text: "Price per unit, stock location, delivery, shipping regions and returns up front. Add to cart or send an offer." },
                ]).map((s) => (
                  <figure key={s.title}>
                    <div className="border-2 border-foreground overflow-hidden bg-card">
                      <img src={s.img} alt={`HIDNstock platform: ${s.title}`} loading="lazy" className="w-full h-auto block" />
                    </div>
                    <figcaption className="mt-4">
                      <h3 className="text-xl font-black uppercase">{s.title}</h3>
                      <p className="text-muted-foreground mt-1">{s.text}</p>
                    </figcaption>
                  </figure>
                ))}
              </div>
            {serviceTab === "browse" && (
              <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-px border-2 border-foreground bg-foreground/15">
                {[
                  { icon: Search, title: "Browse excess", text: "Filter verified lots of deadstock and sorted secondhand by category, material, condition and location." },
                  { icon: Package, title: "List material", text: "Snap photos or import a sheet. Set price, minimum order and condition, then choose to show your brand or sell anonymously." },
                  { icon: Handshake, title: "Offers and requests", text: "Buyers send offers or request to buy. Sellers accept, counter or decline, all in your inbox." },
                  { icon: CheckCircle, title: "Checkout and delivery", text: "Pay by card or invoice. The seller is paid once the order is confirmed, and every order is tracked." },
                ].map((f) => (
                  <div key={f.title} className="p-8 bg-background">
                    <f.icon className="h-8 w-8 mb-4" />
                    <h3 className="text-xl font-black uppercase mb-2">{f.title}</h3>
                    <p className="text-muted-foreground">{f.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <TradeConfidence />
      </div>
    </Layout>
  );
}
